import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Alert,
  IconButton,
  Link,
  CircularProgress,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import { GROUP_NAME } from '../App';
import BskyIcon from './BskyIcon';
import { Instagram } from '@mui/icons-material';

import { apiv1 } from '../api/apiv1';

export default function Contact() {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);


  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setShowError(false);
    setShowSuccess(false);

    try {
      const response = await apiv1.submitContactForm(formData);

      if (response.success) {
        setShowSuccess(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setShowSuccess(false), 8000);
      } else {
        throw new Error(response.error || 'Failed to submit contact form');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setErrorMessage(error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.');
      setShowError(true);
      setTimeout(() => setShowError(false), 8000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" component="h1" gutterBottom>
          Contact Us
        </Typography>
        <Typography variant="h6" color="text.secondary" paragraph>
          Get in touch with {GROUP_NAME} to learn more about our mission or get involved.
        </Typography>
      </Box>

      <Grid container spacing={6}>
        {/* Contact Information */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Get in Touch
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <EmailIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="subtitle2">Email</Typography>
                  <Link href="mailto:info@ccrai.org" color="text.secondary">
                    info@ccrai.org
                  </Link>
                </Box>
              </Box>

              {/* <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <PhoneIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="subtitle2">Phone</Typography>
                  <Link href="tel:+1-303-555-0123" color="text.secondary">
                    (303) 555-0123
                  </Link>
                </Box>
              </Box> */}

              {/* <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <LocationOnIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="subtitle2">Address</Typography>
                  <Typography variant="body2" color="text.secondary">
                    1234 Colorado Ave<br />
                    Denver, CO 80202
                  </Typography>
                </Box>
              </Box> */}

              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Follow Us
                </Typography>
                <Box>
                  {/* <IconButton color="primary" href="https://facebook.com" target="_blank">
                    <FacebookIcon />
                  </IconButton> */}
                  <IconButton color="primary" href="https://bsky.app/profile/ccrai.bsky.social" target="_blank">
                    <BskyIcon />
                  </IconButton>
                  <IconButton color="primary" href="https://www.instagram.com/ccraiofficial/" target="_blank">
                    <Instagram />
                </IconButton>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Contact Form */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Send us a Message
              </Typography>
              
              {showSuccess && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  Thank you for your message! We'll get back to you soon.
                </Alert>
              )}

              {showError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {errorMessage}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      sx={{
                        '& .MuiInputBase-input': {
                          lineHeight: 1.4375,
                        },
                      }}
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      sx={{
                        '& .MuiInputBase-input': {
                          lineHeight: 1.4375,
                        },
                      }}
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      sx={{
                        '& .MuiInputBase-input': {
                          lineHeight: 1.4375,
                        },
                      }}
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Message"
                      name="message"
                      multiline
                      minRows={4}
                      maxRows={10}
                      value={formData.message}
                      onChange={handleChange}
                      required
                      sx={{
                        '& .MuiInputBase-root': {
                          alignItems: 'flex-start',
                          padding: '16.5px 14px',
                          minHeight: '120px',
                        },
                        '& .MuiInputBase-input': {
                          lineHeight: 1.4375,
                          padding: 0,
                          minHeight: '88px !important',
                        },
                        '& .MuiOutlinedInput-root': {
                          '& textarea': {
                            resize: 'vertical',
                            lineHeight: 1.4375,
                            minHeight: '88px',
                            height: '88px',
                          },
                        },
                      }}
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12 }}>
                    <Button 
                      type="submit" 
                      variant="contained" 
                      color="primary" 
                      size="large"
                      disabled={isSubmitting}
                      fullWidth
                      startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Additional Information */}
      {/* <Box sx={{ mt: 8, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>
          Office Hours
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Monday - Friday: 9:00 AM - 5:00 PM (MST)
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Saturday: 10:00 AM - 2:00 PM (MST)
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Sunday: Closed
        </Typography>
      </Box> */}
    </Container>
  );
}
